import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/Input";
import { Error } from "@/components/Error";
import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { OrderType } from "@/redux/services/order/order.type";
import { ORDER_TYPE_LABELS } from "./types";
import { getApiErrorMessage, translateApiErrorText } from "@/lib/apiErrors";
import { Bike, MapPin, User, Users } from "lucide-react";
import { useAppSelector } from "@/redux/features/hooks";
import { useGetRestaurantCustomersQuery } from "@/redux/services/customer.api";
import { RestaurantCustomer } from "@/redux/services/customer.type";
import { useDebounce } from "@/hooks/useDebounce";
import { formatPhoneNumber, parseMoneyToCents } from "@/lib/utils";
import { isNetworkError, NETWORK_ERROR_MESSAGE } from "./networkError";

export interface ConfirmOrderDetails {
  deliveryAddress?: string;
  deliveryFee?: number;
  customerPhone?: string;
  customerFullName?: string;
  deliveryPersonName?: string;
  deliveryPersonPhone?: string;
  /** Number of guests served — dine-in only, when the restaurant asks for it. */
  guestCount?: number;
}

interface ConfirmOrderFormData {
  customerFullName?: string;
  customerPhone?: string;
  deliveryAddress?: string;
  deliveryFee?: string;
  deliveryPersonName?: string;
  deliveryPersonPhone?: string;
  guestCount?: string;
}

interface ConfirmOrderModalProps {
  open: boolean;
  onClose: () => void;
  orderType: OrderType;
  /** Show the dine-in guest-count field (RestaurantSettings.shouldAskForGuestCount). */
  askGuestCount?: boolean;
  onConfirm: (details: ConfirmOrderDetails) => Promise<void> | void;
}

const CREATE_ORDER_ERROR = "Erreur lors de la création de la commande";

// French text for one violation (shared map, SFRESTO-163); never the raw key.
const translateViolation = (message: string, fallback: string): string =>
  translateApiErrorText(message) ?? fallback;

const FORM_FIELDS: ReadonlyArray<keyof ConfirmOrderFormData> = [
  "customerFullName",
  "customerPhone",
  "deliveryAddress",
  "deliveryFee",
  "deliveryPersonName",
  "deliveryPersonPhone",
];

const ConfirmOrderModal: React.FC<ConfirmOrderModalProps> = ({
  open,
  onClose,
  orderType,
  askGuestCount = false,
  onConfirm,
}) => {
  const isDelivery = orderType === "delivery";
  const showGuestCount = askGuestCount && orderType === "dine_in";

  const schema = useMemo(
    () =>
      yup.object({
        customerFullName: yup.string(),
        customerPhone: isDelivery
          ? yup.string().required("Le numéro de téléphone est requis")
          : yup.string(),
        deliveryAddress: isDelivery
          ? yup.string().required("L'adresse de livraison est requise")
          : yup.string(),
        deliveryFee: yup
          .string()
          .test(
            "money",
            "Veuillez entrer un montant valide",
            (value) => {
              if (!value?.trim()) return true;
              const cents = parseMoneyToCents(value);
              return cents !== null && cents >= 0;
            }
          ),
        deliveryPersonName: yup.string(),
        deliveryPersonPhone: yup.string(),
        guestCount: yup.string(),
      }),
    [isDelivery]
  );

  const {
    register,
    handleSubmit,
    setError,
    reset,
    watch,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<ConfirmOrderFormData>({
    resolver: yupResolver(schema),
  });

  const [submitError, setSubmitError] = useState<string | null>(null);
  const [hasPickedCustomer, setHasPickedCustomer] = useState(false);

  useEffect(() => {
    if (!open) {
      reset();
      setSubmitError(null);
      setHasPickedCustomer(false);
    }
  }, [open, reset]);

  const restaurantId = useAppSelector(
    (state) => state.user.currentRestaurant?.id
  );
  const customerPhoneValue = watch("customerPhone") ?? "";
  const debouncedPhone = useDebounce(customerPhoneValue.trim(), 400);
  const shouldSearch =
    open && !hasPickedCustomer && debouncedPhone.length >= 3 && !!restaurantId;

  const { data: matches, isFetching: isSearching } =
    useGetRestaurantCustomersQuery(
      {
        restaurantId: restaurantId ?? "",
        "customer.phone": debouncedPhone,
        limit: 5,
      },
      { skip: !shouldSearch }
    );

  const suggestions = shouldSearch ? matches?.results ?? [] : [];

  const handleSelectCustomer = (match: RestaurantCustomer) => {
    setValue("customerPhone", match.customer.phone, { shouldValidate: true });
    setValue("customerFullName", match.customer.fullName ?? "", {
      shouldValidate: true,
    });
    setHasPickedCustomer(true);
  };

  const onSubmit = async (data: ConfirmOrderFormData) => {
    const details: ConfirmOrderDetails = {};

    if (data.customerPhone?.trim())
      details.customerPhone = data.customerPhone.trim();
    if (data.customerFullName?.trim())
      details.customerFullName = data.customerFullName.trim();

    if (showGuestCount && data.guestCount?.trim()) {
      const parsed = parseInt(data.guestCount, 10);
      if (Number.isFinite(parsed) && parsed > 0) details.guestCount = parsed;
    }

    if (isDelivery) {
      if (data.deliveryAddress?.trim())
        details.deliveryAddress = data.deliveryAddress.trim();
      const deliveryFeeCents = parseMoneyToCents(data.deliveryFee);
      if (deliveryFeeCents !== null) details.deliveryFee = deliveryFeeCents;
      if (data.deliveryPersonName?.trim())
        details.deliveryPersonName = data.deliveryPersonName.trim();
      if (data.deliveryPersonPhone?.trim())
        details.deliveryPersonPhone = data.deliveryPersonPhone.trim();
    }

    setSubmitError(null);
    try {
      await onConfirm(details);
      reset();
      onClose();
    } catch (error: unknown) {
      const data = (
        error as {
          data?: {
            violations?: Array<{ propertyPath: string; message: string }>;
            detail?: string;
          };
        }
      )?.data;
      const violations = data?.violations;
      if (violations?.length) {
        const unmapped: string[] = [];
        for (const v of violations) {
          if ((FORM_FIELDS as ReadonlyArray<string>).includes(v.propertyPath)) {
            setError(v.propertyPath as keyof ConfirmOrderFormData, {
              type: "server",
              message: translateViolation(v.message, "Valeur invalide."),
            });
          } else {
            unmapped.push(translateViolation(v.message, CREATE_ORDER_ERROR));
          }
        }
        if (unmapped.length) {
          setSubmitError([...new Set(unmapped)].join(" · "));
        }
      } else if (isNetworkError(error)) {
        setSubmitError(NETWORK_ERROR_MESSAGE);
      } else {
        setSubmitError(getApiErrorMessage(error, CREATE_ORDER_ERROR));
      }
    }
  };

  const handleClose = () => {
    if (isSubmitting) return;
    reset();
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && handleClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Confirmer — {ORDER_TYPE_LABELS[orderType]}</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4 pt-2">
          {submitError && (
            <div className="rounded-md border border-danger/30 bg-danger/10 px-3 py-2 text-sm text-danger">
              {submitError}
            </div>
          )}

          {/* Customer info */}
          <div className="space-y-3">
            <p className="text-sm font-medium text-muted-foreground flex items-center gap-1.5">
              <User className="h-4 w-4" />
              Client
              {!isDelivery && (
                <span className="text-xs font-normal">(optionnel)</span>
              )}
            </p>
            <div className="relative">
              <Input
                variant="secondary"
                type="tel"
                placeholder="Téléphone du client"
                {...register("customerPhone", {
                  onChange: () => {
                    if (hasPickedCustomer) setHasPickedCustomer(false);
                  },
                })}
                error={errors.customerPhone?.message}
              />
              <Error error={errors.customerPhone?.message} />
              {shouldSearch && !isSearching && suggestions.length > 0 && (
                <ul className="absolute z-50 left-0 right-0 mt-1 max-h-56 overflow-y-auto rounded-lg border border-gray-200 bg-white shadow-md">
                  {suggestions.map((match) => (
                    <li key={match.id}>
                      <button
                        type="button"
                        onClick={() => handleSelectCustomer(match)}
                        className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 flex flex-col"
                      >
                        <span className="font-medium text-dark">
                          {match.customer.fullName || "Sans nom"}
                        </span>
                        <span className="text-xs text-muted-foreground">
                          {formatPhoneNumber(match.customer.phone)}
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div>
              <Input
                variant="secondary"
                placeholder="Nom du client"
                {...register("customerFullName")}
                error={errors.customerFullName?.message}
              />
              <Error error={errors.customerFullName?.message} />
            </div>
          </div>

          {/* Guest count — dine-in only, when the restaurant asks for it */}
          {showGuestCount && (
            <div className="space-y-3">
              <p className="text-sm font-medium text-muted-foreground flex items-center gap-1.5">
                <Users className="h-4 w-4" />
                Nombre de couverts{" "}
                <span className="text-xs font-normal">(optionnel)</span>
              </p>
              <div>
                <Input
                  variant="secondary"
                  type="number"
                  min={1}
                  placeholder="Ex. 4"
                  {...register("guestCount")}
                  error={errors.guestCount?.message}
                />
                <Error error={errors.guestCount?.message} />
              </div>
            </div>
          )}

          {/* Delivery-specific fields */}
          {isDelivery && (
            <>
              <div className="space-y-3">
                <p className="text-sm font-medium text-muted-foreground flex items-center gap-1.5">
                  <MapPin className="h-4 w-4" />
                  Livraison
                </p>
                <div>
                  <Input
                    variant="secondary"
                    placeholder="Adresse de livraison"
                    {...register("deliveryAddress")}
                    error={errors.deliveryAddress?.message}
                  />
                  <Error error={errors.deliveryAddress?.message} />
                </div>
                <div>
                  <Input
                    variant="secondary"
                    type="number"
                    placeholder="Frais de livraison"
                    {...register("deliveryFee")}
                    error={errors.deliveryFee?.message}
                  />
                  <Error error={errors.deliveryFee?.message} />
                </div>
              </div>

              <div className="space-y-3">
                <p className="text-sm font-medium text-muted-foreground flex items-center gap-1.5">
                  <Bike className="h-4 w-4" />
                  Livreur{" "}
                  <span className="text-xs font-normal">(optionnel)</span>
                </p>
                <div>
                  <Input
                    variant="secondary"
                    placeholder="Nom du livreur"
                    {...register("deliveryPersonName")}
                    error={errors.deliveryPersonName?.message}
                  />
                  <Error error={errors.deliveryPersonName?.message} />
                </div>
                <div>
                  <Input
                    variant="secondary"
                    type="tel"
                    placeholder="Téléphone du livreur"
                    {...register("deliveryPersonPhone")}
                    error={errors.deliveryPersonPhone?.message}
                  />
                  <Error error={errors.deliveryPersonPhone?.message} />
                </div>
              </div>
            </>
          )}

          <div className="flex gap-2 pt-1">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              className="flex-1"
              size="lg"
              disabled={isSubmitting}
            >
              Annuler
            </Button>
            <Button
              type="submit"
              className="flex-1"
              size="lg"
              disabled={isSubmitting}
            >
              {isSubmitting ? "Confirmation..." : "Confirmer"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default ConfirmOrderModal;
